import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Button, ChevronRightIcon } from '@motorway/mw-highway-code';
import { SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import { Modal } from 'Storybook/components/local/Modal/Modal';

import processingPaymentVideoMp4 from '../../../../../../client/assets_seller/videos/processing-payment.mp4';
import processingPaymentVideo from '../../../../../../client/assets_seller/videos/processing-payment.webm';
import successTickVideoMp4 from '../../../../../../client/assets_seller/videos/success-tick.mp4';
import successTickVideo from '../../../../../../client/assets_seller/videos/success-tick.webm';
import { PAYMENT_PAGE_EVENTS } from '../../../../../utilities/analytics/analyticsPaymentPage';
import { isIOS, isSafari } from '../../../../../utilities/devices/devices';
import PaymentsTexts from '../../../payments/Payments.text.json';

import styles from './ProcessingPaymentModal.module.scss';

const LocalT = new MDText(PaymentsTexts.payments.enterView);

type ProcessingState = 'processing' | 'success' | 'error';

type ProcessingPaymentModalProps = {
  className?: cx.Argument;
  onClose: () => void;
  onViewSaleSummary?: () => void;
  state?: ProcessingState;
  vrm?: string;
};

export const ProcessingPaymentModal: React.FC<ProcessingPaymentModalProps> = ({
  className,
  onClose,
  onViewSaleSummary,
  state = 'processing',
  vrm,
}) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state === 'error') {
      onClose();
    }
  }, [state, onClose]);

  const isProcessing = state === 'processing';
  const isBusy = busy || isProcessing;

  const preferMp4 = isIOS() || isSafari();

  const handleClick = () => {
    setBusy(true);
    onViewSaleSummary?.();
  };

  const handleClose = () => {
    if (state === 'success') {
      // Track success modal closed
      PAYMENT_PAGE_EVENTS.VEHICLE_PAYMENT_SUCCESS_MODAL_CLOSED(vrm);
      // In success state, closing modal should redirect to sale summary like the button
      setBusy(true);
      onViewSaleSummary?.();
    } else {
      // Track processing modal closed
      PAYMENT_PAGE_EVENTS.VEHICLE_PAYMENT_PROCESSING_MODAL_CLOSED(vrm);
      onClose();
    }
  };

  return (
    <Modal
      centered
      className={styles.processingPaymentModal}
      contentClass={cx(styles.modalContent, className)}
      dialogClass={styles.modal}
      dismissModal={handleClose}
      size={SIZE.SMALL}
    >
      <div key={state} className={styles.container}>
        <div className={cx(styles.title, { [styles.titleSuccess]: !isProcessing })}>
          {LocalT.translate(isProcessing ? 'processingPaymentModal.title' : 'successViewModal.title')}
        </div>
        <div className={styles.videoContainer}>
          <video
            key={`video-${state}`}
            autoPlay
            loop
            muted
            playsInline
            className={cx(styles.processingVideo, { [styles.successVideo]: !isProcessing })}
            preload="auto"
          >
            {preferMp4 ? (
              <>
                <source src={isProcessing ? processingPaymentVideoMp4 : successTickVideoMp4} type="video/mp4" />
                <source src={isProcessing ? processingPaymentVideo : successTickVideo} type="video/webm" />
              </>
            ) : (
              <>
                <source src={isProcessing ? processingPaymentVideo : successTickVideo} type="video/webm" />
                <source src={isProcessing ? processingPaymentVideoMp4 : successTickVideoMp4} type="video/mp4" />
              </>
            )}
            {LocalT.translate('processingPaymentModal.noVideoSupport')}
          </video>
        </div>
        <div className={styles.content}>
          {LocalT.translate(isProcessing ? 'processingPaymentModal.content' : 'successViewModal.content')}
        </div>
        <Button
          fullWidth
          className={cx(styles.saleSummary, { [styles.actionButton]: !isProcessing })}
          disabled={isBusy}
          icon={ChevronRightIcon}
          label={LocalT.translate('successViewModal.button')}
          loading={isBusy}
          onClick={handleClick}
          size={SIZE.MEDIUM}
          variant={VARIANT.PRIMARY}
        />
      </div>
    </Modal>
  );
};
